import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { css, styled } from "styled-components";

import { typeScale } from "lib/themeUtils";

export enum ButtonVariants {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  INVERTED = "inverted",
  LINK = "link",
}

const variants = (props: { variant?: ButtonVariants }) =>
  ({
    [ButtonVariants.PRIMARY]: css`
      color: ${(props) => props.theme.colours.white};
      background: ${(props) => props.theme.colours.primary};

      &:hover {
        background: ${(props) => props.theme.colours.blue[800]};
      }
    `,
    [ButtonVariants.SECONDARY]: css`
      color: ${(props) => props.theme.colours.primary};
      border: 1px solid ${(props) => props.theme.colours.primary};
      background: ${(props) => props.theme.colours.white};

      &:hover {
        background: ${(props) => props.theme.colours.blue[50]};
      }
    `,
    [ButtonVariants.INVERTED]: css`
      color: ${(props) => props.theme.colours.white};
      border: 1px solid ${(props) => props.theme.colours.white};
      background: transparent;

      &:hover {
        background: ${(props) => props.theme.colours.blue[600]};
      }
    `,
    [ButtonVariants.LINK]: css`
      color: ${(props) => props.theme.colours.primary};
      border: none;
      background: transparent;
      padding: 0.2rem 0.5rem;
      text-decoration: underline;

      &:hover {
        background: ${(props) => props.theme.colours.blue[50]};
      }
    `,
  })[props.variant || ButtonVariants.PRIMARY];

export const Button = styled.button.attrs(({ type = "button" }) => ({ type }))<{ variant?: ButtonVariants }>`
  ${typeScale.md}

  cursor: pointer;
  display: inline-block;
  padding: 0.5rem 1.5rem;
  font-weight: bold;

  border-radius: 6px;
  border: none;
  transition: all 200ms ease-in-out;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${(props) => props.theme.colours.focus};
  }

  &:disabled {
    opacity: 0.2;
  }

  ${variants}
`;

export const NavigateButton: React.FC<Omit<React.ComponentProps<typeof Button>, "onClick"> & { to: string }> = ({
  to,
  ...restProps
}) => {
  const navigate = useNavigate();
  const handleNavigate = useCallback(() => navigate(to), [to, navigate]);
  return <Button {...restProps} onClick={handleNavigate} />;
};
